import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const passwordChecks = [
    { label: 'Tối thiểu 6 ký tự', valid: password.length >= 6 },
    { label: 'Mật khẩu khớp', valid: confirmPassword.length > 0 && password === confirmPassword },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Mật khẩu xác nhận không khớp'); return; }
    if (password.length < 6) { toast.error('Mật khẩu tối thiểu 6 ký tự'); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success('Đăng ký thành công');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi đăng ký');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex">
      {/* Left - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden block text-center text-3xl font-bold tracking-wider text-brand-600 uppercase mb-2">June</Link>

          <h1 className="text-2xl font-bold text-gray-900 text-center mb-1">Tạo tài khoản</h1>
          <p className="text-sm text-gray-500 text-center mb-8">Tham gia June để nhận ưu đãi hấp dẫn</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Họ tên</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none transition-colors"
                placeholder="Nguyễn Văn A"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mật khẩu</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-11 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none transition-colors"
                  placeholder="Tối thiểu 6 ký tự"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Xác nhận mật khẩu</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none transition-colors"
                placeholder="Nhập lại mật khẩu"
              />
            </div>

            {/* Password strength indicators */}
            {password.length > 0 && (
              <div className="flex gap-4">
                {passwordChecks.map((check, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${check.valid ? 'bg-green-500' : 'bg-gray-200'}`}>
                      {check.valid && <Check size={10} className="text-white" />}
                    </div>
                    <span className={`text-xs ${check.valid ? 'text-green-600' : 'text-gray-400'}`}>{check.label}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-brand-500 text-white text-sm font-semibold rounded-xl hover:bg-brand-600 disabled:opacity-50 transition-all active:scale-[0.98]"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Đăng ký <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-xs text-gray-400 uppercase tracking-wider">hoặc</span>
            </div>
          </div>

          <p className="text-sm text-center text-gray-500">
            Đã có tài khoản?{' '}
            <Link to="/login" className="text-brand-600 font-semibold hover:text-brand-700 transition-colors">Đăng nhập</Link>
          </p>
        </div>
      </div>

      {/* Right - Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-bl from-brand-500 via-brand-600 to-brand-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-10 right-20 w-72 h-72 border border-white/30 rounded-full" />
          <div className="absolute bottom-10 -left-10 w-96 h-96 border border-white/20 rounded-full" />
          <div className="absolute top-1/3 right-1/4 w-48 h-48 border border-white/25 rounded-full" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <Link to="/" className="text-4xl font-bold tracking-wider uppercase mb-6">June</Link>
          <h2 className="text-3xl font-bold leading-tight mb-4">
            Gia nhập cộng đồng<br />thời trang June
          </h2>
          <p className="text-brand-100 text-lg leading-relaxed max-w-md mb-10">
            Tạo tài khoản miễn phí để nhận thông tin ưu đãi, theo dõi đơn hàng và trải nghiệm mua sắm tuyệt vời.
          </p>
          <div className="space-y-4">
            {['Miễn phí vận chuyển đơn từ 500K', 'Ưu đãi dành riêng cho thành viên', 'Đổi trả dễ dàng trong 30 ngày'].map((text, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check size={14} />
                </div>
                <span className="text-brand-100">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
